
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link, ExternalLink } from "lucide-react";

const ApiDocsReference = () => {
  return (
    <Card>
      <CardHeader>
        <CardTitle>Documentação de Referência</CardTitle>
        <CardDescription>
          Links para documentação oficial das APIs dos fabricantes de inversores
        </CardDescription>
      </CardHeader>
      <CardContent>
        <Tabs defaultValue="growatt">
          <TabsList className="mb-4">
            <TabsTrigger value="growatt">Growatt</TabsTrigger>
            <TabsTrigger value="solis">Solis</TabsTrigger>
            <TabsTrigger value="deye">Deye</TabsTrigger>
            <TabsTrigger value="sofar">Sofar</TabsTrigger>
            <TabsTrigger value="saj">SAJ</TabsTrigger>
          </TabsList>
          
          <TabsContent value="growatt">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Growatt API</h3>
              <p>
                A API Growatt permite monitoramento e controle de inversores Growatt, 
                fornecendo acesso a dados de produção e status.
              </p>
              
              <h4 className="font-medium mt-4">Detalhes da API:</h4>
              <ul className="list-disc list-inside space-y-2">
                <li>URL Base: <code>https://openapi.growatt.com</code></li>
                <li>Autenticação: API Key + Token</li>
                <li>Formato: JSON</li>
                <li>Rate Limits: 10 requisições por minuto</li>
              </ul>
              
              <h4 className="font-medium mt-4">Principais Endpoints:</h4>
              <ul className="list-disc list-inside space-y-2">
                <li><code>/device/list</code> - Lista todos os dispositivos</li>
                <li><code>/plant/list</code> - Lista todas as plantas solares</li>
                <li><code>/device/data</code> - Obtém dados de um dispositivo específico</li>
                <li><code>/plant/energy</code> - Obtém dados de produção de energia</li>
              </ul>
              
              <div className="flex items-center mt-4">
                <Link className="h-4 w-4 mr-2" />
                <a 
                  href="https://openapi.growatt.com/docs" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline flex items-center"
                >
                  Acessar documentação oficial
                  <ExternalLink className="h-3 w-3 ml-1" />
                </a>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="solis">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Solis API</h3>
              <p>
                A API Solis Cloud fornece acesso aos dados dos inversores Solis, 
                permitindo integração com sistemas de monitoramento.
              </p>
              
              <h4 className="font-medium mt-4">Detalhes da API:</h4>
              <ul className="list-disc list-inside space-y-2">
                <li>URL Base: <code>https://api.soliscloud.com</code></li>
                <li>Autenticação: API Key + Secret</li>
                <li>Formato: JSON</li>
                <li>Assinatura HMAC necessária para autenticação</li>
              </ul>
              
              <h4 className="font-medium mt-4">Principais Endpoints:</h4>
              <ul className="list-disc list-inside space-y-2">
                <li><code>/api/v1/inverterList</code> - Lista inversores</li>
                <li><code>/api/v1/powerStationList</code> - Lista estações</li>
                <li><code>/api/v1/inverterDetail</code> - Detalhes do inversor</li>
                <li><code>/api/v1/powerStationData</code> - Dados da estação</li>
              </ul>
              
              <div className="flex items-center mt-4">
                <Link className="h-4 w-4 mr-2" />
                <a 
                  href="https://api.soliscloud.com/docs/en" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline flex items-center"
                >
                  Acessar documentação oficial
                  <ExternalLink className="h-3 w-3 ml-1" />
                </a>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="deye">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Deye API</h3>
              <p>
                A API Deye permite acessar dados de inversores Deye através do
                sistema de monitoramento DeyeCloud.
              </p>
              
              <h4 className="font-medium mt-4">Detalhes da API:</h4>
              <ul className="list-disc list-inside space-y-2">
                <li>URL Base: <code>https://api.deyeinverter.com</code></li>
                <li>Autenticação: Username + Password ou API Token</li>
                <li>Formato: JSON</li>
              </ul>
              
              <h4 className="font-medium mt-4">Principais Endpoints:</h4>
              <ul className="list-disc list-inside space-y-2">
                <li><code>/api/v1/sites</code> - Lista instalações</li>
                <li><code>/api/v1/inverters</code> - Lista inversores</li>
                <li><code>/api/v1/energy</code> - Dados de produção</li>
                <li><code>/api/v1/status</code> - Status dos dispositivos</li>
              </ul>
              
              <div className="flex items-center mt-4">
                <Link className="h-4 w-4 mr-2" />
                <a 
                  href="https://api.deyeinverter.com/documentation" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline flex items-center"
                >
                  Acessar documentação oficial
                  <ExternalLink className="h-3 w-3 ml-1" />
                </a>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="sofar">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">Sofar API</h3>
              <p>
                A API Sofar permite acessar dados dos inversores Sofar Solar
                através da plataforma de monitoramento.
              </p>
              
              <h4 className="font-medium mt-4">Detalhes da API:</h4>
              <ul className="list-disc list-inside space-y-2">
                <li>URL Base: <code>https://api.solarmanpv.com</code></li>
                <li>Autenticação: API Key + Secret</li>
                <li>Formato: JSON</li>
                <li>Requer registro como integrador</li>
              </ul>
              
              <h4 className="font-medium mt-4">Principais Endpoints:</h4>
              <ul className="list-disc list-inside space-y-2">
                <li><code>/v1/stations</code> - Lista estações</li>
                <li><code>/v1/devices</code> - Lista dispositivos</li>
                <li><code>/v1/real-time-data</code> - Dados em tempo real</li>
                <li><code>/v1/history-data</code> - Dados históricos</li>
              </ul>
              
              <div className="flex items-center mt-4">
                <Link className="h-4 w-4 mr-2" />
                <a 
                  href="https://www.solarmanpv.com/developer/index.html" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline flex items-center"
                >
                  Acessar documentação oficial
                  <ExternalLink className="h-3 w-3 ml-1" />
                </a>
              </div>
            </div>
          </TabsContent>
          
          <TabsContent value="saj">
            <div className="space-y-4">
              <h3 className="text-lg font-semibold">SAJ API</h3>
              <p>
                A API SAJ Electric permite monitoramento e controle de inversores SAJ,
                fornecendo acesso a dados de produção e estado dos equipamentos.
              </p>
              
              <h4 className="font-medium mt-4">Detalhes da API:</h4>
              <ul className="list-disc list-inside space-y-2">
                <li>URL Base: <code>https://api.saj-electric.com</code></li>
                <li>Autenticação: OAuth 2.0 ou API Key</li>
                <li>Formato: JSON</li>
              </ul>
              
              <h4 className="font-medium mt-4">Principais Endpoints:</h4>
              <ul className="list-disc list-inside space-y-2">
                <li><code>/api/v2/plants</code> - Lista plantas solares</li>
                <li><code>/api/v2/inverters</code> - Lista inversores</li>
                <li><code>/api/v2/production</code> - Dados de produção</li>
                <li><code>/api/v2/alarms</code> - Alertas e alarmes</li>
              </ul>
              
              <div className="flex items-center mt-4">
                <Link className="h-4 w-4 mr-2" />
                <a 
                  href="https://www.saj-electric.com/developer-portal" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline flex items-center"
                >
                  Acessar documentação oficial
                  <ExternalLink className="h-3 w-3 ml-1" />
                </a>
              </div>
            </div>
          </TabsContent>
        </Tabs>
      </CardContent>
    </Card>
  );
};

export default ApiDocsReference;
